import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";

import type { UiTheme } from "../theme";
import { resolveHeadlineIndex, type ChartSeriesAnnotation } from "./chartAnnotations";
import { LineSwatch } from "./LineSwatch";

// Split out of `WagmiLineChart.tsx` (the 500-line cap). The footer shown when
// `showMeta` is on: the range, and with 2+ curves the one "which curve" key,
// a swatch and the series' `name` each (#388). The headline series' value is
// the host's head (ChartHead), so only the other curves print theirs here.
// #389: the swatch is a sample of the curve's own line (LineSwatch): its
// colour, width and dash, so the key cannot show a line the plot does not.

type Props = {
  built: ChartSeriesAnnotation[];
  theme: UiTheme;
  lo: number;
  hi: number;
  yFormat: (y: number) => string;
};

export function WagmiChartLegend({ built, theme, lo, hi, yFormat }: Props) {
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const headline = built[resolveHeadlineIndex(built)];
  const drawn = built.filter((b) => b.dots.length > 0);

  return (
    <View style={styles.meta}>
      <Text style={styles.range}>{`${yFormat(lo)} – ${yFormat(hi)}`}</Text>
      {drawn.length > 1 ? (
        <View style={styles.key}>
          {drawn.map((b) => (
            <View key={`key-${b.key}`} style={styles.entry}>
              <LineSwatch theme={theme} variant={b.variant} stroke={b.stroke} />
              {b.name ? <Text style={styles.name}>{b.name}</Text> : null}
              {b === headline ? null : (
                <Text style={b.variant === "secondary" ? styles.curMuted : styles.curStrong}>
                  {yFormat(b.last)}
                </Text>
              )}
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, typography: t, spacing: s } = theme;
  const mono = { fontFamily: t.fonts.mono, fontVariant: ["tabular-nums" as const] };
  return StyleSheet.create({
    meta: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "space-between",
      alignItems: "center",
      columnGap: s.s3,
      rowGap: s.s1,
      paddingTop: 2,
    },
    range: { ...mono, color: p.ink3, fontSize: t.sizes.sm },
    key: { flexDirection: "row", flexWrap: "wrap", alignItems: "center", columnGap: s.s3, rowGap: s.s1 },
    // Swatch, name and value never touch; the entry wraps with the key.
    entry: { flexDirection: "row", flexWrap: "wrap", alignItems: "center", columnGap: s.s2, rowGap: s.s1 },
    name: { color: p.ink2, fontSize: t.sizes.sm },
    curStrong: { ...mono, color: p.ink, fontSize: t.sizes.sm, fontWeight: "700" },
    curMuted: { ...mono, color: p.ink3, fontSize: t.sizes.sm, fontWeight: "600" },
  });
}
